import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import PrivateLayout from "../../layouts/PrivateLayout";
import useDecodedToken from "../../utils/useDecodedToken";
import { fetchRaidSignupAuditLog, adminDeleteRaidSignup } from "../../api/api";
import { CLASS_COLORS } from "../../data/wowClasses";
import "@fortawesome/fontawesome-free/css/all.min.css";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const ACTION_BADGES = {
  create: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40",
  update: "text-blue-300 bg-blue-500/10 border-blue-500/40",
  delete: "text-red-300 bg-red-500/10 border-red-500/40",
};

const ActionBadge = ({ action }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded-md border text-xs font-semibold uppercase tracking-wide ${
      ACTION_BADGES[action] || "text-gray-300 bg-gray-500/10 border-gray-500/40"
    }`}
  >
    {action || "—"}
  </span>
);

const FLEX_META = [
  { key: "flexTank", label: "T", color: "text-blue-400" },
  { key: "flexHealer", label: "H", color: "text-green-400" },
  { key: "flexDps", label: "D", color: "text-red-400" },
];

const FlexBadges = ({ entry }) => (
  <div className="flex items-center gap-1">
    {FLEX_META.map(({ key, label, color }) => (
      <span
        key={key}
        className={`w-5 h-5 flex items-center justify-center rounded text-[10px] font-bold ${
          entry[key] ? `${color} bg-white/5` : "text-gray-700"
        }`}
      >
        {label}
      </span>
    ))}
  </div>
);

const AUDIT_GRID_COLS =
  "grid-cols-[1fr_0.6fr_0.6fr_1fr_1.1fr_0.6fr_1.7fr_0.9fr]";

function RaidSignupAuditLog() {
  const { token } = useDecodedToken();
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [removingSignupId, setRemovingSignupId] = useState(null);

  const loadData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const data = await fetchRaidSignupAuditLog(token);
      setEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching raid sign-up audit log:", error);
      setErrorMessage(error.message || "Failed to load audit log.");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopySignupId = async (signupId) => {
    try {
      await navigator.clipboard.writeText(signupId);
      toast.success("Sign-up ID copied to clipboard.");
    } catch (error) {
      console.error("Failed to copy sign-up ID:", error);
      toast.error("Failed to copy sign-up ID.");
    }
  };

  const handleRemoveSignup = async (entry) => {
    const confirmed = window.confirm(
      `Remove this sign-up (${entry.name}${entry.characterName ? ` — ${entry.characterName}` : ""})? This cannot be undone.`
    );
    if (!confirmed) return;

    setRemovingSignupId(entry.signupId);
    try {
      await adminDeleteRaidSignup(token, entry.signupId);
      toast.success("Sign-up removed.");
      await loadData();
    } catch (error) {
      console.error("Error removing raid sign-up:", error);
      toast.error(error.message || "Failed to remove sign-up.");
    } finally {
      setRemovingSignupId(null);
    }
  };

  return (
    <PrivateLayout backgroundImage="/wow-wallpaper.jpg">
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white font-serif tracking-wide mb-2">
                Raid Sign-Up Audit Log
              </h1>
              <p className="text-gray-300 text-sm sm:text-base">
                Admin-only view of changes made to raid sign-ups.
              </p>
            </div>
            <button
              type="button"
              onClick={loadData}
              disabled={isLoading}
              className="flex items-center gap-2 text-gray-300 hover:text-white border border-gray-600 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200"
            >
              <i className={`fas fa-arrows-rotate ${isLoading ? "animate-spin" : ""}`}></i>
              <span>Refresh</span>
            </button>
          </div>

          {errorMessage && (
            <div className="mb-6 bg-red-500/20 border border-red-500/40 rounded-lg px-4 py-3 text-red-300 text-sm text-center">
              {errorMessage}
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-300 text-sm">Loading audit log...</p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl overflow-x-auto">
              <div
                className={`grid ${AUDIT_GRID_COLS} gap-x-3 bg-white/90 px-4 py-3 text-xs font-semibold uppercase tracking-widest text-gray-700 min-w-[1100px]`}
              >
                <div>Timestamp</div>
                <div>Action</div>
                <div>Team</div>
                <div>Player</div>
                <div>Character</div>
                <div>Flex</div>
                <div>Actor / Sign-up ID</div>
                <div>Remove</div>
              </div>
              <div className="divide-y divide-white/5 bg-gray-900/60 min-w-[1100px]">
                {entries.length === 0 ? (
                  <div className="px-4 py-6 text-center text-gray-400 text-sm">
                    No audit log entries yet.
                  </div>
                ) : (
                  entries.map((entry) => {
                    const alreadyRemoved = entry.action === "delete";
                    const isRemoving = removingSignupId === entry.signupId;
                    return (
                      <div
                        key={entry.id}
                        className={`grid ${AUDIT_GRID_COLS} gap-x-3 items-center px-4 py-3 text-sm`}
                      >
                        <div className="text-gray-300">
                          {formatTimestamp(entry.createdAt)}
                        </div>
                        <div>
                          <ActionBadge action={entry.action} />
                        </div>
                        <div className="text-gray-300 truncate" title={entry.team}>
                          {entry.team || "—"}
                        </div>
                        <div className="text-white font-medium truncate" title={entry.name}>
                          {entry.name || "—"}
                        </div>
                        <div
                          className="min-w-0 truncate"
                          title={entry.realm ? `${entry.characterName} — ${entry.realm}` : entry.characterName}
                        >
                          <span style={{ color: CLASS_COLORS[entry.className] || "#D1D5DB" }}>
                            {entry.characterName || "—"}
                          </span>
                          {entry.spec && (
                            <span className="text-gray-500 ml-1">({entry.spec})</span>
                          )}
                        </div>
                        <FlexBadges entry={entry} />
                        <div className="text-xs text-gray-400 space-y-0.5 min-w-0">
                          <div className="truncate" title={entry.actorUserId || entry.actorIp || ""}>
                            Actor: {entry.actorUserId || entry.actorIp || "—"}
                          </div>
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="truncate" title={entry.signupId}>
                              ID: {entry.signupId}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCopySignupId(entry.signupId)}
                              title="Copy sign-up ID"
                              className="flex-shrink-0 text-gray-500 hover:text-white transition-colors duration-200"
                            >
                              <i className="fas fa-copy"></i>
                            </button>
                          </div>
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => handleRemoveSignup(entry)}
                            disabled={alreadyRemoved || isRemoving}
                            title={alreadyRemoved ? "This sign-up was already removed" : "Remove this sign-up"}
                            className="flex items-center gap-1.5 text-red-300 hover:text-white border border-red-500/40 hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-200"
                          >
                            <i className={`fas ${isRemoving ? "fa-spinner animate-spin" : "fa-trash"}`}></i>
                            <span>Remove</span>
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </PrivateLayout>
  );
}

export default RaidSignupAuditLog;
